/** @jsxImportSource @emotion/react */
/**
 * PropertyForm — Dynamic form for event template properties
 * Renders inputs based on template property definitions
 */
import React from 'react';
import { css } from '@emotion/react';
import {
  Form,
  InputNumber,
  Input,
  Select,
  Switch,
  Radio,
  Typography,
  Divider,
  Space,
  Tag,
  Spin,
} from 'antd';
import type { EventTemplateProperty } from '../../types/eventTemplates';

const { Text } = Typography;
const { TextArea } = Input;

const styles = {
  form: css({
    maxWidth: 480,
  }),
  quantityRow: css({
    background: '#f0f5ff',
    borderRadius: 8,
    padding: '12px 16px',
    marginBottom: 16,
  }),
  requiredLabel: css({
    '&::after': {
      content: '" *"',
      color: '#ff4d4f',
    },
  }),
  previousValue: css({
    fontSize: 12,
    color: '#8c8c8c',
    fontStyle: 'italic',
  }),
};

export interface PropertyFormValues {
  [propertyId: string]: string | number | boolean | null;
}

interface PropertyFormProps {
  /** Template properties to render */
  properties: EventTemplateProperty[];
  /** Current form values */
  values: PropertyFormValues;
  /** Value change handler */
  onChange: (propertyId: number, value: any) => void;
  /** Notes value */
  notes: string;
  /** Notes change handler */
  onNotesChange: (notes: string) => void;
  /** Show quantity field for batch plantings */
  showQuantity?: boolean;
  /** Total planting quantity */
  plantingQuantity?: number;
  /** Quantity affected value */
  quantityAffected?: number | null;
  /** Quantity change handler */
  onQuantityChange?: (value: number | null) => void;
  /** Show units/percent switch for quantity input */
  allowQuantityPercent?: boolean;
  /** Current quantity input mode */
  quantityMode?: 'units' | 'percent';
  /** Quantity mode change handler */
  onQuantityModeChange?: (mode: 'units' | 'percent') => void;
  /** Percent value for quantity input */
  quantityPercent?: number | null;
  /** Percent change handler */
  onQuantityPercentChange?: (value: number | null) => void;
  /** Label for quantity input */
  quantityLabel?: string;
  /** Helper text below quantity input */
  quantityHelper?: string | null;
  /** Loading state */
  loading?: boolean;
  /** Previous attribute values (for reference) */
  previousValues?: Record<number, { value: string | number | null; date?: string }>;
}

const PropertyForm: React.FC<PropertyFormProps> = ({
  properties,
  values,
  onChange,
  notes,
  onNotesChange,
  showQuantity = false,
  plantingQuantity,
  quantityAffected,
  onQuantityChange,
  allowQuantityPercent = false,
  quantityMode = 'units',
  onQuantityModeChange,
  quantityPercent,
  onQuantityPercentChange,
  quantityLabel,
  quantityHelper,
  loading = false,
  previousValues,
}) => {
  // Hide service/marker properties (e.g. harvesting, pruning_form, quantity_affected) — they carry
  // no user input; the action is identified by the template itself.
  const renderableProperties = properties.filter(
    (prop) => !prop.isService && prop.code !== 'quantity_affected'
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin tip="Загрузка свойств..." />
      </div>
    );
  }

  const renderInput = (prop: EventTemplateProperty) => {
    const key = String(prop.propertyId);
    const currentValue = values[key];
    const prev = previousValues?.[prop.propertyId];

    switch (prop.dataType) {
      case 'number':
      case 'integer':
      case 'decimal':
      case 'float':
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <InputNumber
              style={{ width: '100%' }}
              placeholder={prop.unit ? `Введите значение (${prop.unit})` : 'Введите число'}
              value={currentValue as number}
              onChange={(val) => onChange(prop.propertyId, val)}
              addonAfter={prop.unit || undefined}
              precision={prop.code === 'quantity_kg' ? 3 : (prop.dataType === 'decimal' ? 2 : 0)}
              step={prop.code === 'quantity_kg' ? 0.001 : undefined}
              min={prop.code === 'quantity_kg' ? 0.001 : undefined}
              readOnly={prop.isReadonly}
            />
            {prev && (
              <Text css={styles.previousValue}>
                Прошлое: {prev.value}{prop.unit ? ` ${prop.unit}` : ''}
                {prev.date ? ` (${prev.date})` : ''}
              </Text>
            )}
          </Space>
        );

      case 'text':
        if (prop.options && prop.options.length > 0) {
          return (
            <Select
              style={{ width: '100%' }}
              placeholder="Выберите значение"
              value={currentValue as string}
              onChange={(val) => onChange(prop.propertyId, val)}
              allowClear
              disabled={prop.isReadonly}
              options={prop.options.map((opt) => ({
                value: opt.value,
                label: opt.value,
              }))}
            />
          );
        }
        return (
          <Input
            placeholder="Введите текст"
            value={currentValue as string}
            onChange={(e) => onChange(prop.propertyId, e.target.value)}
            readOnly={prop.isReadonly}
          />
        );

      case 'boolean':
        return (
          <Switch
            checked={!!currentValue}
            onChange={(val) => onChange(prop.propertyId, val)}
            disabled={prop.isReadonly}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        );

      case 'date':
      case 'datetime':
        return (
          <Input
            type={prop.dataType === 'date' ? 'date' : 'datetime-local'}
            value={currentValue as string}
            onChange={(e) => onChange(prop.propertyId, e.target.value)}
            readOnly={prop.isReadonly}
          />
        );

      default:
        return (
          <Input
            placeholder="Значение"
            value={currentValue as string}
            onChange={(e) => onChange(prop.propertyId, e.target.value)}
          />
        );
    }
  };

  return (
    <div css={styles.form}>
      {/* Batch quantity */}
      {showQuantity && plantingQuantity && plantingQuantity > 1 && (
        <div css={styles.quantityRow}>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Space>
              <Text strong>Групповая посадка:</Text>
              <Tag color="blue">всего {plantingQuantity} шт</Tag>
            </Space>
            {allowQuantityPercent && (
              <Radio.Group
                value={quantityMode}
                onChange={(e) => onQuantityModeChange?.(e.target.value)}
                optionType="button"
                buttonStyle="solid"
                size="small"
              >
                <Radio.Button value="units">шт</Radio.Button>
                <Radio.Button value="percent">%</Radio.Button>
              </Radio.Group>
            )}
            <Form.Item label={quantityLabel || 'Обработано растений'} style={{ marginBottom: 0 }}>
              {allowQuantityPercent && quantityMode === 'percent' ? (
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  max={100}
                  value={quantityPercent}
                  onChange={onQuantityPercentChange}
                  placeholder="от 1 до 100"
                  addonAfter={`% = ${quantityAffected ?? 0} из ${plantingQuantity}`}
                />
              ) : (
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  max={plantingQuantity}
                  value={quantityAffected}
                  onChange={onQuantityChange}
                  placeholder={`от 1 до ${plantingQuantity}`}
                  addonAfter={`из ${plantingQuantity}`}
                />
              )}
            </Form.Item>
            {quantityHelper && (
              <Text type="secondary">{quantityHelper}</Text>
            )}
          </Space>
        </div>
      )}

      {/* Properties */}
      {renderableProperties.length > 0 && (
        <>
          <Divider orientation="left" orientationMargin={0} style={{ marginTop: 8 }}>
            Свойства
          </Divider>
          <Form layout="vertical" size="middle">
            {renderableProperties.map((prop) => (
              <Form.Item
                key={prop.propertyId}
                label={
                  <span css={prop.isRequired ? styles.requiredLabel : undefined}>
                    {prop.name}
                    {prop.unit && <Text type="secondary"> ({prop.unit})</Text>}
                  </span>
                }
                required={prop.isRequired}
              >
                {renderInput(prop)}
              </Form.Item>
            ))}
          </Form>
        </>
      )}

      {/* Notes */}
      <Divider orientation="left" orientationMargin={0}>
        Заметки
      </Divider>
      <TextArea
        rows={3}
        placeholder="Комментарий к событию (необязательно)"
        value={notes}
        onChange={(e) => onNotesChange(e.target.value)}
        maxLength={1000}
        showCount
      />
    </div>
  );
};

export default PropertyForm;
